import { useState, useEffect } from "react";
import { getApiUrl } from "@/utils/api";

// Backend status values
export type StudyStatus =
    | "PreEnrollment"
    | "EnrollmentOpenSubmissionBlocked"
    | "EnrollmentOpen"
    | "EnrollmentClosingSoon"
    | "EnrollmentClosedSubmissionOpen"
    | "StudyClosed";

// Legacy status mapping for backwards compatibility
export type LegacyStudyStatus =
    | "EnrollmentNotYetOpen"
    | "EnrollmentOpen"
    | "EnrollmentExtension"
    | "DataSubmissionOnly"
    | "StudyClosed"
    | "StudyClosedResultsAvailable";

export type StepOverride = "enabled" | "disabled" | null;

export interface StudyStepOverrides {
    enroll: StepOverride;
    edit_participants: StepOverride;
    survey: StepOverride;
    upload_payroll: StepOverride;
}

export interface StudyStatusData {
    tenant: string;
    study_year: number;
    data_year: number;
    status: StudyStatus;
    dates: {
        enrollment_start: string;
        enrollment_end: string;
        submission_start: string;
        submission_end: string;
        warning_date: string;
    };
    is_enrollment_open: boolean;
    is_submission_open: boolean;
    messages: {
        banner: string;
        deadline: string;
    };
    step_overrides?: StudyStepOverrides;
    /** The cycle before the served one, or null when this is the tenant's first. */
    previous_study?: StudyStatusData | null;
}

/** The prior cycle, as much of it as the public pages need to describe it. */
export interface PreviousCycle {
    studyYear: number;
    statusMessage: string;
    isSubmissionOpen: boolean;
    stepOverrides: StudyStepOverrides;
}

export interface UseStudyStatusReturn {
    status: LegacyStudyStatus | null;
    studyYear: number;
    dataYear: number;
    isEnrollmentOpen: boolean;
    isSubmissionOpen: boolean;
    deadlineDate: string;
    statusMessage: string;
    enrollmentStartDate: string;
    enrollmentEndDate: string;
    submissionStartDate: string;
    stepOverrides: StudyStepOverrides;
    /**
     * The cycle before the served one. The landing pages speak about two at once — the new study's
     * enrollment date alongside the previous study being closed — and the served cycle rolls over
     * as soon as the next one is configured. Null when the tenant has no earlier cycle.
     */
    previousStudy: PreviousCycle | null;
    loading: boolean;
    error: string | null;
}

const EMPTY_STEP_OVERRIDES: StudyStepOverrides = {
    enroll: null,
    edit_participants: null,
    survey: null,
    upload_payroll: null,
};

/** Resolve a step override to a `disabled` attribute value. `null` returns `fallbackDisabled`. */
export function resolveStepDisabled(override: StepOverride, fallbackDisabled: boolean): boolean {
    if (override === "enabled") return false;
    if (override === "disabled") return true;
    return fallbackDisabled;
}

// Map backend status to legacy frontend status for backwards compatibility
const mapBackendStatusToLegacy = (backendStatus: StudyStatus): LegacyStudyStatus => {
    switch (backendStatus) {
        case "PreEnrollment":
            return "EnrollmentNotYetOpen";
        case "EnrollmentOpenSubmissionBlocked":
            return "EnrollmentOpen"; // Enrollment open, but submission not yet available
        case "EnrollmentOpen":
            return "EnrollmentOpen";
        case "EnrollmentClosingSoon":
            return "EnrollmentExtension";
        case "EnrollmentClosedSubmissionOpen":
            return "DataSubmissionOnly";
        case "StudyClosed":
            return "StudyClosed";
        default:
            return "StudyClosed";
    }
};

export function useStudyStatus(_tenant: string, mockDate?: Date): UseStudyStatusReturn {
    const [data, setData] = useState<StudyStatusData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStudyStatus = async () => {
            try {
                setLoading(true);

                // Build URL with optional mock_date parameter
                const apiUrl = getApiUrl("/study/status");
                // Handle both absolute and relative URLs
                const baseUrl = apiUrl.startsWith("http")
                    ? apiUrl
                    : typeof window !== "undefined"
                      ? `${window.location.origin}${apiUrl}`
                      : apiUrl;
                const url = new URL(baseUrl);
                if (mockDate) {
                    url.searchParams.set("mock_date", mockDate.toISOString());
                }

                const response = await fetch(url.toString(), {
                    headers: {
                        "Content-Type": "application/json",
                    },
                });

                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}: Failed to fetch study status`);
                }

                const apiData: StudyStatusData = await response.json();
                setData(apiData);
                setError(null);
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to load study status");
                console.error("Error fetching study status:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchStudyStatus();
    }, [_tenant, mockDate]);

    if (!data) {
        return {
            status: null,
            studyYear: 2026,
            dataYear: 2025,
            isEnrollmentOpen: false,
            isSubmissionOpen: false,
            deadlineDate: "",
            statusMessage: "",
            enrollmentStartDate: "",
            enrollmentEndDate: "",
            submissionStartDate: "",
            stepOverrides: EMPTY_STEP_OVERRIDES,
            previousStudy: null,
            loading,
            error,
        };
    }

    // Map backend status to legacy status for existing components
    const legacyStatus = mapBackendStatusToLegacy(data.status);

    const previous = data.previous_study;
    const previousStudy: PreviousCycle | null = previous
        ? {
              studyYear: previous.study_year,
              statusMessage: previous.messages.banner,
              isSubmissionOpen: previous.is_submission_open,
              stepOverrides: previous.step_overrides ?? EMPTY_STEP_OVERRIDES,
          }
        : null;

    return {
        status: legacyStatus,
        studyYear: data.study_year,
        dataYear: data.data_year,
        isEnrollmentOpen: data.is_enrollment_open,
        isSubmissionOpen: data.is_submission_open,
        deadlineDate: data.dates.submission_end,
        statusMessage: data.messages.banner,
        enrollmentStartDate: data.dates.enrollment_start,
        enrollmentEndDate: data.dates.enrollment_end,
        submissionStartDate: data.dates.submission_start.slice(0, 10),
        stepOverrides: data.step_overrides ?? EMPTY_STEP_OVERRIDES,
        previousStudy,
        loading,
        error,
    };
}
